import Box from '@mui/material/Box';
import React from 'react';

import { QMenuDivider, QMenuItem, QMenuSub, QMenuSubheader } from 'components/QDropdowns';

/**
 * NestedQMenu-shaped option used by txn action option hooks.
 * Kept while hooks still build arrays; rendered via {@link renderTxnMenuOptions}.
 */
export type TxnMenuOption = {
  id?: string;
  key?: string;
  label?: React.ReactNode;
  subtext?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  divider?: boolean;
  isSubheader?: boolean;
  /** Fake-check rows migrated to QMenuItem toggle. */
  variant?: 'toggle';
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Error-emphasis action (Delete). */
  destructive?: boolean;
  onSelect?: () => void;
  subMenu?: TxnMenuOption[];
  /** Rare non-row chrome (e.g. split exclude banner). */
  customRender?: React.ReactNode;
};

function optionKey(option: TxnMenuOption, index: number): string {
  if (option.id) return option.id;
  if (option.key) return String(option.key);
  if (typeof option.label === 'string' && option.label) return `${option.label}-${index}`;
  return `txn-menu-option-${index}`;
}

/**
 * Maps NestedQMenu-shaped txn options onto QDropdowns primitives.
 */
export function renderTxnMenuOptions(options: TxnMenuOption[]): React.ReactNode[] {
  return options.flatMap((option, index) => {
    const key = optionKey(option, index);
    const nodes: React.ReactNode[] = [];

    if (option.isSubheader) {
      nodes.push(<QMenuSubheader key={key} label={typeof option.label === 'string' ? option.label : ''} />);
    } else if (option.customRender) {
      nodes.push(
        <QMenuItem
          key={key}
          id={option.id}
          variant='action'
          label={
            <Box component='span' sx={{ display: 'block', width: '100%' }}>
              {option.customRender}
            </Box>
          }
          closeOnClick={false}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        />,
      );
    } else if (option.subMenu) {
      nodes.push(
        <QMenuSub
          key={key}
          id={option.id}
          label={typeof option.label === 'string' ? option.label : key}
          startIcon={option.startIcon}
        >
          {renderTxnMenuOptions(option.subMenu)}
        </QMenuSub>,
      );
    } else if (option.variant === 'toggle') {
      nodes.push(
        <QMenuItem
          key={key}
          id={option.id}
          variant='toggle'
          label={option.label ?? ''}
          checked={Boolean(option.checked)}
          onCheckedChange={(checked) => option.onCheckedChange?.(checked)}
          startIcon={option.startIcon}
          endIcon={option.endIcon}
          disabled={option.disabled}
        />,
      );
    } else {
      nodes.push(
        <QMenuItem
          key={key}
          id={option.id}
          variant='action'
          label={option.label ?? ''}
          subtext={option.subtext}
          startIcon={option.startIcon}
          endIcon={option.endIcon}
          disabled={option.disabled}
          destructive={option.destructive}
          onClick={() => option.onSelect?.()}
        />,
      );
    }

    if (option.divider) {
      nodes.push(<QMenuDivider key={`${key}-divider`} />);
    }

    return nodes;
  });
}
